import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { EmployeeCategory, ID } from '@shared/types';
import { buildBaseFields, buildUpdateFields, fromSnapshot, writeAuditLog } from '@shared/utils';

const employeeCategoryCreateSchema = z.object({
  name: z.string().min(1, '社員分類名は必須です'),
  priority: z.number().default(0),
});

export type EmployeeCategoryCreateInput = z.infer<typeof employeeCategoryCreateSchema>;
export type EmployeeCategoryUpdateInput = Partial<EmployeeCategoryCreateInput>;

const COL = 'employeeCategories';

export async function fetchEmployeeCategories(): Promise<EmployeeCategory[]> {
  const q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    orderBy('priority')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as EmployeeCategory);
}

export async function fetchEmployeeCategory(id: ID): Promise<EmployeeCategory | null> {
  const snap = await getDoc(doc(db, COL, id));
  return fromSnapshot<EmployeeCategory>(snap);
}

export async function createEmployeeCategory(
  data: EmployeeCategoryCreateInput,
  userId: ID
): Promise<EmployeeCategory> {
  const validated = parseOrThrow(employeeCategoryCreateSchema, data);
  const payload = { ...validated, ...buildBaseFields(userId) };
  const ref = await addDoc(collection(db, COL), payload);
  const created = { id: ref.id, ...payload } as unknown as EmployeeCategory;
  await writeAuditLog({
    entityType: 'employeeCategory',
    entityId: ref.id,
    action: 'create',
    userId,
    after: validated,
  });
  return created;
}

export async function updateEmployeeCategory(
  id: ID,
  data: EmployeeCategoryUpdateInput,
  userId: ID
): Promise<EmployeeCategory> {
  const validated = employeeCategoryCreateSchema.partial().parse(data);
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('社員分類が見つかりません。');
  const before = snap.data();
  const patch = { ...validated, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'employeeCategory',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: validated,
  });
  return { id, ...before, ...patch } as unknown as EmployeeCategory;
}

export async function deleteEmployeeCategory(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('社員分類が見つかりません。');
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'employeeCategory',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
  });
}
